import React, { createContext, useState, useEffect } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ModulesContext = createContext({});

const ModulesProvider = ({ children }) => {
  const [modules, setModules] = useState({
    moduleCalendar: "enabled",
    moduleInternalChat: "enabled",
    moduleSms: "enabled",
    moduleApi: "enabled",
    moduleAutoSchedule: "enabled",
    moduleFileManager: "enabled"
  });
  const [loading, setLoading] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsLoading, setSmsLoading] = useState(true);
  const [smsBalance, setSmsBalance] = useState(0);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const { data } = await api.get("/settings");
        
        const moduleSettings = {
          moduleCalendar: data.find(s => s.key === "moduleCalendar")?.value || "enabled",
          moduleInternalChat: data.find(s => s.key === "moduleInternalChat")?.value || "enabled",
          moduleSms: data.find(s => s.key === "moduleSms")?.value || "enabled",
          moduleApi: data.find(s => s.key === "moduleApi")?.value || "enabled",
          moduleAutoSchedule: data.find(s => s.key === "moduleAutoSchedule")?.value || "enabled",
          moduleFileManager: data.find(s => s.key === "moduleFileManager")?.value || "enabled"
        };
        
        setModules(moduleSettings);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    const checkSmsConfig = async () => {
      try {
        setSmsLoading(true);
        
        // Tentar buscar saldo SMS primeiro - se der 403, módulo está desativado
        try {
          await api.get("/sms/balance");
          // Se chegou aqui, módulo está ativo
          setSmsEnabled(true);
        } catch (error) {
          // Se retornou 403, módulo está explicitamente desativado
          if (error.response && error.response.status === 403) {
            setSmsEnabled(false);
            setSmsLoading(false);
            return;
          }
          // Outros erros, continua verificando settings
        }
        
        // Buscar configurações do sistema
        const { data: settings } = await api.get("/settings");
        
        // Verificar se módulo SMS está explicitamente habilitado
        const smsModuleEnabled = settings.find(s => s.key === "moduleSms");
        if (smsModuleEnabled && smsModuleEnabled.value === "disabled") {
          setSmsEnabled(false);
          setSmsLoading(false);
          return;
        }
        
        // Verificar se as chaves necessárias existem e estão preenchidas
        // Suporta tanto configuração antiga (comteleApiKey) quanto nova (smsApiKey)
        const apiKeyOld = settings.find(s => s.key === "comteleApiKey");
        const apiKeyNew = settings.find(s => s.key === "smsApiKey");
        const apiUrlOld = settings.find(s => s.key === "comteleApiUrl");
        const apiUrlNew = settings.find(s => s.key === "smsApiUrl");
        
        // Considera configurado se tiver API key e URL (nova ou antiga)
        const hasApiKey = (apiKeyNew && apiKeyNew.value && apiKeyNew.value.trim() !== "") ||
                          (apiKeyOld && apiKeyOld.value && apiKeyOld.value.trim() !== "");
        const hasApiUrl = (apiUrlNew && apiUrlNew.value && apiUrlNew.value.trim() !== "") ||
                          (apiUrlOld && apiUrlOld.value && apiUrlOld.value.trim() !== "");
        
        const isConfigured = hasApiKey && hasApiUrl;
        
        setSmsEnabled(isConfigured);
        
        // Se estiver configurado, buscar o saldo
        if (isConfigured) {
          try {
            const { data } = await api.get("/sms/balance");
            setSmsBalance(data.balance || 0);
          } catch (error) {
            if (error.response && error.response.status === 403) {
              // Se der 403 aqui também, desabilita
              setSmsEnabled(false);
            } else {
              console.warn("Erro ao buscar saldo SMS:", error);
              setSmsBalance(0);
            }
          }
        }
      } catch (error) {
        console.error("Erro ao verificar configuração SMS:", error);
        setSmsEnabled(false);
      } finally {
        setSmsLoading(false);
      }
    };

    fetchModules();
    checkSmsConfig();
  }, []);

  const isModuleEnabled = (moduleName) => {
    return modules[moduleName] === "enabled";
  };

  return (
    <ModulesContext.Provider value={{ modules, loading, isModuleEnabled, smsEnabled, smsLoading, smsBalance }}>
      {children}
    </ModulesContext.Provider>
  );
};

export { ModulesContext, ModulesProvider };
